//formcontact.tsx

import { useState } from 'react';
import { Send, Loader, AlertCircle, CheckCircle } from 'lucide-react';

const API_BASE_URL = import.meta.env?.VITE_BACKEND_URL;

interface FieldErrors {
  [key: string]: string;
}

function FormContact() {
  const [formData, setFormData] = useState({
    nume: '',
    email: '',
    telefon: '',
    companie: '',
    tipTransport: '',
    mesaj: ''
  });
  
  const [status, setStatus] = useState('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [touchedFields, setTouchedFields] = useState<{[key: string]: boolean}>({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Marchează câmpul ca fiind atins
    setTouchedFields(prev => ({
      ...prev,
      [name]: true
    }));
    
    // Validează câmpul în timp real
    validateField(name, value);
    
    // Resetează erorile generale când utilizatorul modifică datele
    if (status === 'error') {
      setStatus('idle');
      setErrorMessage('');
    }
  };

  // Validare în timp real pentru câmpuri individuale
  const validateField = (fieldName: string, value: string) => {
    const errors = { ...fieldErrors };
    
    switch (fieldName) {
      case 'nume':
        if (!value.trim()) {
          errors.nume = 'Numele este obligatoriu';
        } else if (value.trim().length < 2) {
          errors.nume = 'Numele trebuie să aibă cel puțin 2 caractere';
        } else if (value.trim().length > 100) {
          errors.nume = 'Numele nu poate depăși 100 de caractere';
        } else {
          delete errors.nume;
        }
        break;
        
      case 'email':
        const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
        if (!value.trim()) {
          errors.email = 'Email-ul este obligatoriu';
        } else if (!emailRegex.test(value.trim())) {
          errors.email = 'Introduceți un email valid (ex: nume@email.com)';
        } else {
          delete errors.email;
        }
        break;
        
      case 'telefon':
        const cleanPhone = value.replace(/[\s\-\(\)\.]/g, '');
        const phoneRegex = /^(\+40|0040|0)[0-9]{9}$/;
        if (!value.trim()) {
          errors.telefon = 'Numărul de telefon este obligatoriu';
        } else if (!phoneRegex.test(cleanPhone)) {
          errors.telefon = 'Format invalid. Exemple: 0745123456 sau +40745123456';
        } else {
          delete errors.telefon;
        }
        break;
        
      case 'companie':
        if (value && value.length > 100) {
          errors.companie = 'Numele companiei nu poate depăși 100 de caractere';
        } else {
          delete errors.companie;
        }
        break;
        
      case 'mesaj':
        if (!value.trim()) {
          errors.mesaj = 'Mesajul este obligatoriu';
        } else if (value.trim().length < 10) {
          errors.mesaj = 'Mesajul trebuie să aibă cel puțin 10 caractere';
        } else if (value.trim().length > 2000) {
          errors.mesaj = 'Mesajul nu poate depăși 2000 de caractere';
        } else {
          delete errors.mesaj;
        }
        break;
    }
    
    setFieldErrors(errors);
  };

  // Validare completă a formularului
  const validateForm = () => {
    const errors: string[] = [];
    const newFieldErrors: FieldErrors = {};
    
    // Validează toate câmpurile obligatorii
    if (!formData.nume.trim()) {
      errors.push('Numele este obligatoriu');
      newFieldErrors.nume = 'Numele este obligatoriu';
    } else if (formData.nume.trim().length < 2) {
      errors.push('Numele trebuie să aibă cel puțin 2 caractere');
      newFieldErrors.nume = 'Numele trebuie să aibă cel puțin 2 caractere';
    }
    
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    if (!formData.email.trim()) {
      errors.push('Email-ul este obligatoriu');
      newFieldErrors.email = 'Email-ul este obligatoriu';
    } else if (!emailRegex.test(formData.email.trim())) {
      errors.push('Email-ul nu are un format valid');
      newFieldErrors.email = 'Introduceți un email valid (ex: nume@email.com)';
    }
    
    const cleanPhone = formData.telefon.replace(/[\s\-\(\)\.]/g, '');
    const phoneRegex = /^(\+40|0040|0)[0-9]{9}$/;
    if (!formData.telefon.trim()) {
      errors.push('Numărul de telefon este obligatoriu');
      newFieldErrors.telefon = 'Numărul de telefon este obligatoriu';
    } else if (!phoneRegex.test(cleanPhone)) {
      errors.push('Numărul de telefon nu este valid');
      newFieldErrors.telefon = 'Format invalid. Exemple: 0745123456 sau +40745123456';
    }
    
    if (!formData.mesaj.trim()) {
      errors.push('Mesajul este obligatoriu');
      newFieldErrors.mesaj = 'Mesajul este obligatoriu';
    } else if (formData.mesaj.trim().length < 10) {
      errors.push('Mesajul trebuie să aibă cel puțin 10 caractere');
      newFieldErrors.mesaj = 'Mesajul trebuie să aibă cel puțin 10 caractere';
    }
    
    setFieldErrors(newFieldErrors);
    
    // Marchează toate câmpurile ca fiind atinse pentru a afișa erorile
    setTouchedFields({
      nume: true,
      email: true,
      telefon: true,
      companie: true,
      tipTransport: true,
      mesaj: true
    });
    
    return errors;
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    
    console.log('=== FORM SUBMIT START ===');
    console.log('Current status:', status);
    console.log('Form data being sent:', formData);
    
    if (status === 'loading') {
      console.log('Already loading, returning');
      return;
    }

    // Validare completă
    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      console.log('Validation failed:', validationErrors);
      setErrorMessage('Vă rugăm să corectați erorile de mai jos');
      setStatus('error');
      return;
    }

    console.log('Validation passed, setting status to loading');
    setStatus('loading');
    setErrorMessage('');
    setFieldErrors({});

    try {
      const dataToSend = {
        nume: formData.nume.trim(),
        email: formData.email.trim(),
        telefon: formData.telefon.trim().replace(/[\s\-\(\)\.]/g, ''),
        companie: formData.companie.trim(),
        tipTransport: formData.tipTransport,
        mesaj: formData.mesaj.trim()
      };

      console.log('Data being sent to backend:', JSON.stringify(dataToSend, null, 2));
      console.log('Making fetch request to:', `${API_BASE_URL}/form/contact`);
      
      const response = await fetch(`${API_BASE_URL}/form/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(dataToSend),
      });

      console.log('Response status:', response.status);
      const result = await response.json();
      console.log('Response result:', result);

      if (!response.ok) {
        // Dacă backend-ul returnează erori specifice pentru câmpuri
        if (result.errors && Array.isArray(result.errors)) {
          const newFieldErrors: FieldErrors = {};
          result.errors.forEach((error: any) => {
            if (error.field) {
              newFieldErrors[error.field] = error.message;
            }
          });
          setFieldErrors(newFieldErrors);
          setErrorMessage('Vă rugăm să corectați erorile de mai jos');
        } else {
          setErrorMessage(result.message || `Eroare server: ${response.status}`);
        }
        throw new Error(result.message || `HTTP error! status: ${response.status}`);
      }

      if (result.success) {
        console.log('Success! Message sent successfully');
        setStatus('success');
        
        // Reset form data
        setFormData({
          nume: '',
          email: '',
          telefon: '',
          companie: '',
          tipTransport: '',
          mesaj: ''
        });
        
        // Reset field errors și touched fields
        setFieldErrors({});
        setTouchedFields({});
        
        // Auto-reset după 8 secunde
        setTimeout(() => setStatus('idle'), 8000);
      } else {
        throw new Error(result.message || 'Eroare la trimiterea mesajului');
      }

    } catch (error) {
      console.error('=== FORM SUBMIT ERROR ===');
      console.error('Error details:', error);
      
      setStatus('error');
      if (!errorMessage) {
        setErrorMessage(error.message || 'Eroare la trimiterea mesajului. Încercați din nou.');
      }
      
      // Auto-reset după 6 secunde
      setTimeout(() => {
        setStatus('idle');
        setErrorMessage('');
      }, 6000);
    }
    
    console.log('=== FORM SUBMIT END ===');
  };

  // Funcție pentru a determina stilul câmpului
  const getFieldStyle = (fieldName: string, baseStyle: string) => {
    const hasError = fieldErrors[fieldName] && touchedFields[fieldName];
    const isValid = touchedFields[fieldName] && !fieldErrors[fieldName] && formData[fieldName];
    
    if (hasError) {
      return `${baseStyle} border-red-400 focus:border-red-500 focus:ring-red-200`;
    } else if (isValid) {
      return `${baseStyle} border-green-400 focus:border-green-500 focus:ring-green-200`;
    }
    return `${baseStyle} border-white/20 focus:border-white/40 focus:ring-white/20`;
  };

  // Componentă pentru afișarea erorilor la câmp
  const FieldError = ({ fieldName }: { fieldName: string }) => {
    if (!fieldErrors[fieldName] || !touchedFields[fieldName]) return null;
    
    return (
      <div className="flex items-center mt-2 text-red-400 text-sm">
        <AlertCircle className="w-4 h-4 mr-1 flex-shrink-0" />
        <span>{fieldErrors[fieldName]}</span>
      </div>
    );
  };

  // Componentă pentru afișarea validării pozitive
  const FieldValid = ({ fieldName }: { fieldName: string }) => {
    if (fieldErrors[fieldName] || !touchedFields[fieldName] || !formData[fieldName]) return null;
    
    return (
      <div className="flex items-center mt-2 text-green-400 text-sm">
        <CheckCircle className="w-4 h-4 mr-1 flex-shrink-0" />
        <span>Valid</span>
      </div>
    );
  };

  if (status === 'success') {
    return (
      <div className="text-center py-8">
        <div className="max-w-lg mx-auto bg-white/10 backdrop-blur-sm rounded-3xl p-8 border border-white/20">
          <div className="mb-6">
            <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          </div>
          <h3 className="text-2xl font-bold text-white mb-4">
            Mesaj trimis cu succes!
          </h3>
          <p className="text-slate-300 mb-6">
            Mulțumim pentru mesajul dumneavoastră!<br/>
            Echipa noastră vă va contacta în cel mai scurt timp.
          </p>
          <button
            onClick={() => setStatus('idle')}
            className="bg-gradient-to-r from-blue-500 to-teal-500 hover:from-blue-600 hover:to-teal-600 text-white py-3 px-6 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105"
          >
            Trimite alt mesaj
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <h3 className="text-2xl sm:text-3xl font-bold text-white text-center lg:text-left">
        Trimiteți-ne un mesaj
      </h3>
      
      {status === 'error' && (
        <div className="p-6 bg-red-500/10 backdrop-blur-sm border-2 border-red-400/50 rounded-2xl">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-400 mr-3 flex-shrink-0" />
            <div>
              <p className="text-red-400 font-semibold">
                Eroare la trimiterea mesajului
              </p>
              <p className="text-red-300 text-sm mt-1">
                {errorMessage || 'Completați toate câmpurile obligatorii și încercați din nou.'}
              </p>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <input
              type="text"
              name="nume"
              value={formData.nume}
              onChange={handleInputChange}
              placeholder="Nume complet *"
              required
              className={getFieldStyle('nume', "w-full pl-4 pr-4 py-4 bg-white/5 backdrop-blur-sm border-2 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 transition-all duration-200")}
            />
            <FieldError fieldName="nume" />
            <FieldValid fieldName="nume" />
          </div>
          
          <div>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="exemplu@email.com *"
              required
              className={getFieldStyle('email', "w-full pl-4 pr-4 py-4 bg-white/5 backdrop-blur-sm border-2 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 transition-all duration-200")}
            />
            <FieldError fieldName="email" />
            <FieldValid fieldName="email" />
          </div>
          
          <div>
            <input
              type="tel"
              name="telefon"
              value={formData.telefon}
              onChange={handleInputChange}
              placeholder="0745 123 456 *"
              required
              className={getFieldStyle('telefon', "w-full pl-4 pr-4 py-4 bg-white/5 backdrop-blur-sm border-2 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 transition-all duration-200")}
            />
            <FieldError fieldName="telefon" />
            <FieldValid fieldName="telefon" />
          </div>
          
          <div>
            <input
              type="text"
              name="companie"
              value={formData.companie}
              onChange={handleInputChange}
              placeholder="Numele companiei (opțional)"
              className={getFieldStyle('companie', "w-full pl-4 pr-4 py-4 bg-white/5 backdrop-blur-sm border-2 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 transition-all duration-200")}
            />
            <FieldError fieldName="companie" />
            <FieldValid fieldName="companie" />
          </div>
        </div>

        <div>
          <select
            name="tipTransport"
            value={formData.tipTransport}
            onChange={handleInputChange}
            className={getFieldStyle('tipTransport', "w-full pl-4 pr-4 py-4 bg-white/5 backdrop-blur-sm border-2 rounded-xl text-white focus:outline-none focus:ring-2 transition-all duration-200")}
          >
            <option value="" className="bg-slate-800 text-white">Selectați tipul de transport (opțional)</option>
            <option value="national" className="bg-slate-800 text-white">Transport național</option>
            <option value="international" className="bg-slate-800 text-white">Transport internațional</option>
            <option value="fragile" className="bg-slate-800 text-white">Mărfuri fragile</option>
            <option value="special" className="bg-slate-800 text-white">Transport special</option>
            <option value="logistica" className="bg-slate-800 text-white">Servicii logistică</option>
            <option value="altele" className="bg-slate-800 text-white">Altele</option>
          </select>
          <FieldError fieldName="tipTransport" />
          <FieldValid fieldName="tipTransport" />
        </div>

        <div>
          <textarea
            name="mesaj"
            value={formData.mesaj}
            onChange={handleInputChange}
            rows={5}
            placeholder="Descrieți în detaliu cerința dvs. de transport: tipul mărfii, destinația, termenele, etc. *"
            required
            className={getFieldStyle('mesaj', "w-full pl-4 pr-4 py-4 bg-white/5 backdrop-blur-sm border-2 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 resize-none transition-all duration-200")}
          />
          <div className="flex justify-between items-center mt-2">
            <div className="flex-1">
              <FieldError fieldName="mesaj" />
              <FieldValid fieldName="mesaj" />
            </div>
            <div className="text-slate-400 text-sm ml-4">
              {formData.mesaj.length}/2000
            </div>
          </div>
        </div>

        <button
          type="submit"
          disabled={status === 'loading'}
          className={`w-full py-4 px-8 rounded-2xl font-bold text-white text-lg transition-all duration-300 transform ${
            status === 'loading'
              ? 'bg-slate-600 cursor-not-allowed opacity-50'
              : 'bg-gradient-to-r from-blue-500 to-teal-500 hover:from-blue-600 hover:to-teal-600 hover:scale-105 hover:shadow-xl hover:shadow-blue-500/25'
          }`}
        >
          {status === 'loading' ? (
            <div className="flex items-center justify-center">
              <Loader className="w-6 h-6 mr-3 animate-spin" />
              Se trimite mesajul...
            </div>
          ) : (
            <div className="flex items-center justify-center">
              <Send className="w-6 h-6 mr-3" />
              Trimite mesajul
            </div>
          )}
        </button>
        
        <p className="text-slate-400 text-sm text-center">
          * Câmpurile marcate sunt obligatorii
        </p>
      </form>
    </div>
  );
}

export default FormContact;